/* ============================================================================
   AUTH API — thin client for the BFF auth endpoints (Option B).
   The BFF owns the Yperea/QA session (keyed by the Cloudflare Access identity);
   the browser only ever passes the short-lived Firebase ID token.
   ============================================================================ */
import { CONFIG } from '../config';

const base = CONFIG.bffPath;

export interface SessionUser {
  id: string;
  username: string;
  full_name?: string | null;
  email?: string | null;
}

export interface SessionInfo {
  authenticated: boolean;
  user?: SessionUser;
  accessEmail?: string;
  env?: string;
}

export async function getSession(): Promise<SessionInfo> {
  try {
    const r = await fetch(`${base}/session`, { credentials: 'include' });
    if (!r.ok) return { authenticated: false };
    return (await r.json()) as SessionInfo;
  } catch {
    return { authenticated: false };
  }
}

/** Exchange a Firebase ID token for a real Yperea (QA) session, stored by the BFF. */
export async function exchangeFirebase(idToken: string): Promise<SessionInfo> {
  const r = await fetch(`${base}/auth/firebase`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ idToken }),
  });
  const j = (await r.json().catch(() => ({}))) as SessionInfo & { error?: string; detail?: unknown };
  if (!r.ok || !j.authenticated) {
    throw new Error(j.error || `Sign-in exchange failed (${r.status})`);
  }
  return j;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${base}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
}

interface GqlEnvelope<T> {
  data?: T;
  errors?: Array<{ message: string }>;
  error?: string; // BFF-level error (e.g. not_authenticated)
}

/**
 * Run an authenticated GraphQL query against the real backend via the BFF proxy
 * (`POST /api/graphql`). The BFF attaches the stored Yperea/QA token; the browser
 * never holds it. Throws on transport, auth, or GraphQL errors. See plan/07.
 */
export async function gqlQuery<T = unknown>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  const r = await fetch(`${base}/graphql`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ query, variables }),
  });
  const j = (await r.json().catch(() => ({}))) as GqlEnvelope<T>;
  if (!r.ok) {
    throw new Error(
      j.error === 'not_authenticated'
        ? 'Not signed in to the QA backend.'
        : j.error || `GraphQL request failed (${r.status})`,
    );
  }
  if (j.errors && j.errors.length) throw new Error(j.errors.map((e) => e.message).join('; '));
  if (!j.data) throw new Error('No data returned from the backend.');
  return j.data;
}
